import { useEffect, useRef, useState, FC, ReactNode } from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import { Colors } from '@mercator-gcp/grout';
import Markdown from 'markdown-to-jsx';

const displayStyle = {
  lineHeight: 1.6,
  textAlign: 'left',
  width: '100%',
  '& div:first-of-type': {
    '&>:first-child': {
      marginTop: 0,
    },
    '&>:last-child': {
      marginBottom: 0,
    },
    h3: {
      marginTop: 'padding_8',
      fontWeight: 600,
    },
    li: {
      marginLeft: 'padding_16',
      lineHeight: 1.6,
      marginBottom: 'padding_4',
    },
    p: {
      marginTop: 'padding_8',
      marginBottom: 'padding_8',
      lineHeight: 1.6,
    },
    h2: {
      marginTop: 'padding_24',
      marginBottom: 'padding_8',
      fontSize: '16px',
      fontWeight: 600,
      letterSpacing: '-0.31px',
    },
    a: {
      color: Colors.ForestGreen900,
      textDecoration: 'none',
      transition: 'text-decoration 0.2s ease-in-out',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
};

type StreamingTextProps = {
  text: string;
  speed?: number; // milliseconds per word
  onComplete?: () => void;
  sx?: BoxProps['sx'];
};

const InferenceTag: FC<{ children: ReactNode }> = ({ children, ...props }) => {
  // Check if children is a string like "FACT" or "INFERRED"
  const content = Array.isArray(children) ? children.join('') : children;
  const isTag = typeof content === 'string' && /^[A-Z]+$/.test(content);

  if (isTag) {
    return (
      <Box
        as="span"
        display="inline-block"
        bg={Colors.ArmyGreen200}
        color={Colors.ArmyGreen900}
        px="padding_8"
        py="padding_2"
        borderRadius="4px"
        fontSize="10px"
        lineHeight="1"
        mr="padding_4"
        verticalAlign="middle"
      >
        {children}
      </Box>
    );
  }

  return <em {...props}>{children}</em>;
};

const StreamingText = ({
  text,
  speed = 20, // Default: 20ms per word (faster)
  onComplete,
  sx = {},
}: StreamingTextProps) => {
  const [displayedText, setDisplayedText] = useState('');
  const streamedTextRef = useRef<string>('');
  const onCompleteRef = useRef(onComplete);

  // Update ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!text) {
      setDisplayedText('');
      streamedTextRef.current = '';

      return undefined;
    }

    // If already streamed this exact text, don't restart
    if (streamedTextRef.current === text) {
      setDisplayedText(text);

      return undefined;
    }

    // If speed is 0, show content immediately
    if (speed === 0) {
      setDisplayedText(text);
      streamedTextRef.current = text;
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }

      return undefined;
    }

    // Split text into words while preserving spaces
    const words = text.split(/(\s+)/);
    let currentIndex = 0;
    setDisplayedText('');
    streamedTextRef.current = text; // Mark as streaming

    const interval = setInterval(() => {
      if (currentIndex < words.length) {
        setDisplayedText((prev) => prev + words[currentIndex]);
        currentIndex += 1;
      } else {
        clearInterval(interval);
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <Box sx={{ ...displayStyle, ...sx }}>
      {text && (
        <Box
          sx={{
            width: '100%',
            '& > div': {
              '& > *': {
                animation: 'text-fade-in 0.4s ease-out forwards',
              },
            },
          }}
        >
          <Markdown
            options={{
              overrides: {
                // Override the standard <em> tag
                em: {
                  component: InferenceTag,
                },
              },
            }}
          >
            {
              displayedText.replace(/\[([A-Z0-9\s]+)\]/g, '*$1*') // turn [TAG] into *TAG* to convert to badges
            }
          </Markdown>
        </Box>
      )}
    </Box>
  );
};

export default StreamingText;
